import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2, RefreshCw, FileText, ArrowDownUp,
  DollarSign, Clock, CheckCircle, XCircle, TrendingDown,
  ChevronDown, ChevronRight, MapPin, Truck, Search
} from 'lucide-react';
import toast from 'react-hot-toast';

const API_BASE = import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.VITE_API_URL || 'http://localhost:3001') + '/api';

// ─── Types ──────────────────────────────────────────────────────────

interface AccountSummary {
  high_season_credit_line: number | null;
  standard_credit_line: number | null;
  past_due: number | null;
  total_due: number | null;
}

interface DashboardData {
  account_summary: AccountSummary | null;
  invoices: {
    total_open: number;
    open_invoice_total: number;
    credits_received: number;
    credits_received_count: number;
    demo_return_credits: number;
    credits_pending: number;
    credits_pending_count: number;
    unreconciled_credits: number;
    net_payable: number;
  };
  trade_ins: {
    pending_count: number;
    pending_amount: number;
    total_in_process: number;
  };
  compensation: {
    total: number;
    in_process: number;
  };
  aging: Record<string, { count: number; invoices_total: number; credits_total: number }>;
  weekly_due: Array<{
    week_label: string;
    week_start: string;
    invoices_total: number;
    credits_total: number;
    count: number;
  }>;
  credit_line: {
    current_season: string;
    current_limit: number;
    current_utilization_pct: number;
    total_outstanding: number;
    available_credit: number;
    high_season: { limit: number; start: string; end: string };
    standard: { limit: number; start: string; end: string };
    next_transition: { date: string; to: string; new_limit: number };
  };
  scrape_health: Record<string, {
    last_scrapes: any[];
    last_success: any;
    consecutive_failures: number;
  }>;
}

interface Invoice {
  id: string;
  invoice_number: string;
  original_invoice: string | null;
  order_number: string | null;
  po_number: string | null;
  reference: string | null;
  status: string;
  invoice_date: string;
  due_date: string | null;
  amount: number;
  payment_terms: string | null;
  invoice_type: string;
  customer_name: string | null;
  reconciled: boolean;
  first_seen_at: string;
}

interface InvoiceLineItem {
  id: string;
  sku: string | null;
  product_name: string | null;
  quantity: number;
  subtotal: number | null;
  total: number | null;
  shipping_address: string | null;
  shipping_method: string | null;
  tag_name: string | null;
}

interface InvoiceDetail extends Invoice {
  line_items: InvoiceLineItem[];
}

interface TradeStatus {
  id: string;
  trade_number: string;
  customer_name: string | null;
  po_number: string | null;
  status: string;
  date_created: string | null;
  submitted_amount: number | null;
  credit_received: boolean;
  credit_received_at: string | null;
}

// ─── Helpers ────────────────────────────────────────────────────────

const fmt = (n: number | null | undefined) => {
  if (n == null) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

const fmtDate = (d: string | null) => {
  if (!d) return '-';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const invoiceTypeLabel: Record<string, string> = {
  invoice: 'Invoice',
  titu_credit: 'TITU Credit',
  finance_charge: 'Finance Charge',
  demo_return: 'Demo Return',
  other_credit: 'Credit',
};

const invoiceTypeColor: Record<string, string> = {
  invoice: 'bg-blue-100 text-blue-700',
  titu_credit: 'bg-green-100 text-green-700',
  finance_charge: 'bg-orange-100 text-orange-700',
  demo_return: 'bg-purple-100 text-purple-700',
  other_credit: 'bg-gray-100 text-gray-700',
};

// ─── Component ──────────────────────────────────────────────────────

const CallawayDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'invoices' | 'trades'>('overview');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [trades, setTrades] = useState<TradeStatus[]>([]);
  const [invoiceFilter, setInvoiceFilter] = useState<string>('all');
  const [invoicePage, setInvoicePage] = useState(0);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [tradeSearch, setTradeSearch] = useState('');

  // ─── Data Fetching ─────────────────────────────────────────────

  const loadDashboard = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/callaway/dashboard`);
      const data = await resp.json();
      setDashboard(data);
    } catch (error) {
      console.error('Error loading Callaway dashboard:', error);
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '50', offset: String(invoicePage * 50) });
      if (invoiceFilter !== 'all') params.set('invoice_type', invoiceFilter);
      if (invoiceSearch) params.set('search', invoiceSearch);
      const resp = await fetch(`${API_BASE}/callaway/invoices?${params}`);
      const data = await resp.json();
      setInvoices(data.data || []);
      setInvoiceCount(data.count || 0);
    } catch (error) {
      console.error('Error loading invoices:', error);
    }
  }, [invoiceFilter, invoicePage, invoiceSearch]);

  const loadTrades = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (tradeSearch) params.set('search', tradeSearch);
      const resp = await fetch(`${API_BASE}/callaway/trades?${params}`);
      const data = await resp.json();
      setTrades(data.data || []);
    } catch (error) {
      console.error('Error loading trades:', error);
    }
  }, [tradeSearch]);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await fetch(`${API_BASE}/callaway/sync`, { method: 'POST' });
      toast.success('Callaway sync started');
      setTimeout(() => {
        loadDashboard();
        setSyncing(false);
      }, 5000);
    } catch (error) {
      toast.error('Sync failed');
      setSyncing(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([loadDashboard(), loadInvoices(), loadTrades()])
      .finally(() => setLoading(false));
  }, [loadDashboard, loadInvoices, loadTrades]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  // ─── Loading State ─────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-500">Loading Callaway data...</span>
      </div>
    );
  }

  const d = dashboard;

  // ─── Render ────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Callaway Connect</h1>
          <p className="text-sm text-gray-500 mt-1">Invoice tracking, trade-in credits, and reconciliation</p>
        </div>
        <button
          onClick={triggerSync}
          disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>

      {/* Top KPI Cards */}
      {d && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Open Invoices */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Open Invoices</span>
              <FileText className="w-5 h-5 text-blue-500" />
            </div>
            <div className="mt-2">
              <div className="text-2xl font-bold text-gray-900">{fmt(d.invoices.open_invoice_total)}</div>
              <div className="text-xs text-gray-400 mt-1">{d.invoices.total_open} open items</div>
            </div>
          </div>

          {/* Credits Received (on account, unapplied) */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Credits Received</span>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <div className="mt-2">
              <div className="text-2xl font-bold text-green-600">{fmt(d.invoices.credits_received)}</div>
              <div className="text-xs text-gray-400 mt-1">{d.invoices.credits_received_count} TITU credits on account</div>
            </div>
          </div>

          {/* Credits Pending (not yet received from Callaway) */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Credits Pending</span>
              <Clock className="w-5 h-5 text-orange-500" />
            </div>
            <div className="mt-2">
              <div className="text-2xl font-bold text-orange-600">{fmt(d.invoices.credits_pending)}</div>
              <div className="text-xs text-gray-400 mt-1">{d.invoices.credits_pending_count} trade-ins awaiting appraisal</div>
            </div>
          </div>

          {/* Net Payable */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Net Payable</span>
              <DollarSign className="w-5 h-5 text-gray-700" />
            </div>
            <div className="mt-2">
              <div className="text-2xl font-bold text-gray-900">{fmt(d.invoices.net_payable)}</div>
              <div className="text-xs text-gray-400 mt-1">After received credits</div>
            </div>
          </div>

          {/* Projected (after pending credits too) */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-500">Projected Net</span>
              <TrendingDown className="w-5 h-5 text-purple-500" />
            </div>
            <div className="mt-2">
              <div className="text-2xl font-bold text-purple-600">{fmt(d.invoices.net_payable - d.invoices.credits_pending)}</div>
              <div className="text-xs text-gray-400 mt-1">After all pending credits</div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-0">
        {([
          { id: 'overview', label: 'Overview', icon: FileText },
          { id: 'invoices', label: 'Invoices & Credits', icon: DollarSign },
          { id: 'trades', label: 'Trade-In Status', icon: ArrowDownUp },
        ] as const).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 font-medium text-sm border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab dashboard={d} trades={trades} />}
      {activeTab === 'invoices' && (
        <InvoicesTab
          invoices={invoices}
          count={invoiceCount}
          filter={invoiceFilter}
          onFilterChange={(f) => { setInvoiceFilter(f); setInvoicePage(0); }}
          page={invoicePage}
          onPageChange={setInvoicePage}
          search={invoiceSearch}
          onSearchChange={(s) => { setInvoiceSearch(s); setInvoicePage(0); }}
        />
      )}
      {activeTab === 'trades' && (
        <TradesTab
          trades={trades}
          search={tradeSearch}
          onSearchChange={setTradeSearch}
        />
      )}
    </div>
  );
};

// ─── Overview Tab ───────────────────────────────────────────────────

const agingLabels: Record<string, string> = {
  overdue: 'Overdue',
  due_this_week: 'Due This Week',
  due_this_month: 'Due in 8-14 Days',
  due_next_30: 'Due in 15-30 Days',
  due_next_60: 'Due in 31-60 Days',
  due_later: 'Due 60+ Days',
};

const agingColors: Record<string, string> = {
  overdue: 'bg-red-500',
  due_this_week: 'bg-orange-500',
  due_this_month: 'bg-yellow-500',
  due_next_30: 'bg-blue-400',
  due_next_60: 'bg-blue-300',
  due_later: 'bg-gray-300',
};

const OverviewTab: React.FC<{ dashboard: DashboardData | null; trades: TradeStatus[] }> = ({ dashboard, trades }) => {
  const d = dashboard;
  if (!d) return null;

  const healthEntries = Object.entries(d.scrape_health || {});
  const pendingTrades = trades.filter(t => !t.credit_received && t.status === 'In Process');
  const aging = d.aging || {};
  const cl = d.credit_line;

  // Calculate total for aging bar
  const agingTotal = Object.values(aging).reduce((s, b) => s + b.invoices_total, 0);

  return (
    <div className="space-y-6">
      {/* Credit Line & Aging Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Credit Line Status */}
        {cl && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">Credit Line</h3>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">{cl.current_season}</span>
                  <span className="font-medium text-gray-700">{fmt(cl.total_outstanding)} / {fmt(cl.current_limit)}</span>
                </div>
                {/* Utilization bar */}
                <div className="w-full bg-gray-100 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      cl.current_utilization_pct > 90 ? 'bg-red-500' :
                      cl.current_utilization_pct > 75 ? 'bg-orange-500' :
                      'bg-blue-500'
                    }`}
                    style={{ width: `${Math.min(100, cl.current_utilization_pct)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-gray-400">{cl.current_utilization_pct}% utilized</span>
                  <span className="text-green-600 font-medium">{fmt(cl.available_credit)} available</span>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-3 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">High Season (Jan-Jun)</span>
                  <span className="font-medium text-gray-700">{fmt(cl.high_season.limit)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Standard (Jul-Dec)</span>
                  <span className="font-medium text-gray-700">{fmt(cl.standard.limit)}</span>
                </div>
                <div className="flex justify-between text-xs pt-1 border-t border-gray-50">
                  <span className="text-gray-500">Next transition</span>
                  <span className="font-medium text-gray-700">
                    {fmtDate(cl.next_transition.date)} → {cl.next_transition.to} ({fmt(cl.next_transition.new_limit)})
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Aging Breakdown */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900">Invoices Due (Aging)</h3>
          </div>
          <div className="p-5">
            {/* Stacked bar */}
            {agingTotal > 0 && (
              <div className="flex rounded-full h-4 overflow-hidden mb-4">
                {Object.entries(aging).map(([key, bucket]) => {
                  const pct = (bucket.invoices_total / agingTotal) * 100;
                  if (pct < 0.5) return null;
                  return (
                    <div
                      key={key}
                      className={`${agingColors[key]} transition-all`}
                      style={{ width: `${pct}%` }}
                      title={`${agingLabels[key]}: ${fmt(bucket.invoices_total)}`}
                    />
                  );
                })}
              </div>
            )}

            {/* Bucket details */}
            <div className="space-y-2">
              {Object.entries(aging).map(([key, bucket]) => {
                return (
                  <div key={key} className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${agingColors[key]}`} />
                    <span className="text-sm text-gray-600 w-36">{agingLabels[key]}</span>
                    <span className="text-sm font-medium text-gray-900 w-28 text-right">{fmt(bucket.invoices_total)}</span>
                    {bucket.credits_total > 0 && (
                      <span className="text-xs text-green-600 w-24 text-right">-{fmt(bucket.credits_total)}</span>
                    )}
                    <span className="text-xs text-gray-400 ml-auto">{bucket.count} items</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Payment Timeline */}
      {d.weekly_due && d.weekly_due.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900">Weekly Payment Schedule</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-5 py-2.5 text-gray-500 font-medium">Week</th>
                  <th className="text-right px-5 py-2.5 text-gray-500 font-medium">Invoices Due</th>
                  <th className="text-right px-5 py-2.5 text-gray-500 font-medium">Credits</th>
                  <th className="text-right px-5 py-2.5 text-gray-500 font-medium">Net Due</th>
                  <th className="text-center px-5 py-2.5 text-gray-500 font-medium"># Items</th>
                </tr>
              </thead>
              <tbody>
                {d.weekly_due.slice(0, 12).map((week) => {
                  const net = week.invoices_total - week.credits_total;
                  const isPast = new Date(week.week_start) < new Date();
                  return (
                    <tr key={week.week_start} className={`border-b border-gray-50 ${isPast ? 'bg-red-50/50' : 'hover:bg-gray-50'}`}>
                      <td className="px-5 py-2.5 text-gray-700 font-medium">
                        {week.week_label}
                        {isPast && <span className="ml-2 text-xs text-red-500 font-normal">overdue</span>}
                      </td>
                      <td className="px-5 py-2.5 text-right text-gray-900 font-medium">{fmt(week.invoices_total)}</td>
                      <td className="px-5 py-2.5 text-right text-green-600">
                        {week.credits_total > 0 ? `-${fmt(week.credits_total)}` : '-'}
                      </td>
                      <td className={`px-5 py-2.5 text-right font-semibold ${net < 0 ? 'text-green-600' : 'text-gray-900'}`}>
                        {fmt(net)}
                      </td>
                      <td className="px-5 py-2.5 text-center text-gray-500">{week.count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bottom Row: Summary + Pending Trades + Health */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Open Invoice Summary */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900">Balance Breakdown</h3>
          </div>
          <div className="p-5 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Open Invoices</span>
              <span className="text-sm font-semibold text-gray-900">{fmt(d.invoices.open_invoice_total)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Credits Received (on account)</span>
              <span className="text-sm font-semibold text-green-600">-{fmt(d.invoices.credits_received)}</span>
            </div>
            {d.invoices.demo_return_credits > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Demo Return Credits</span>
                <span className="text-sm font-semibold text-green-600">-{fmt(d.invoices.demo_return_credits)}</span>
              </div>
            )}
            <div className="border-t border-gray-100 pt-3 flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Net Payable</span>
              <span className="text-base font-bold text-gray-900">{fmt(d.invoices.net_payable)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Credits Pending (not yet received)</span>
              <span className="text-sm font-semibold text-orange-500">-{fmt(d.invoices.credits_pending)}</span>
            </div>
            <div className="border-t border-gray-100 pt-2 flex justify-between items-center">
              <span className="text-sm font-medium text-gray-500">Projected Net</span>
              <span className="text-sm font-bold text-purple-600">{fmt(d.invoices.net_payable - d.invoices.credits_pending)}</span>
            </div>
          </div>
        </div>

        {/* Pending Trade-Ins */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900">Pending Trade-Ins</h3>
          </div>
          <div className="overflow-x-auto max-h-56 overflow-y-auto">
            {pendingTrades.length === 0 ? (
              <div className="p-5 text-sm text-gray-500 text-center">No pending trade-ins</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-4 py-2 text-gray-500 font-medium">Customer</th>
                    <th className="text-right px-4 py-2 text-gray-500 font-medium">Amount</th>
                    <th className="text-left px-4 py-2 text-gray-500 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingTrades.slice(0, 8).map(t => (
                    <tr key={t.id} className="border-b border-gray-50">
                      <td className="px-4 py-2 text-gray-700 text-xs">{t.customer_name || '-'}</td>
                      <td className="px-4 py-2 text-right text-green-600 font-medium text-xs">{fmt(t.submitted_amount)}</td>
                      <td className="px-4 py-2 text-gray-500 text-xs">{fmtDate(t.date_created)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {pendingTrades.length > 8 && (
            <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-100">
              +{pendingTrades.length - 8} more pending
            </div>
          )}
        </div>

        {/* Scraper Health */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900">Scraper Health</h3>
          </div>
          <div className="p-4 space-y-3">
            {healthEntries.map(([type, info]) => (
              <div key={type} className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50">
                {info.consecutive_failures === 0 ? (
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                )}
                <div>
                  <div className="text-sm font-medium text-gray-900 capitalize">{type.replace('_', ' ')}</div>
                  <div className="text-xs text-gray-500">
                    {info.last_success
                      ? `${new Date(info.last_success.scraped_at).toLocaleDateString()}`
                      : 'No scrapes yet'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Invoices Tab ───────────────────────────────────────────────────

interface InvoicesTabProps {
  invoices: Invoice[];
  count: number;
  filter: string;
  onFilterChange: (f: string) => void;
  page: number;
  onPageChange: (p: number) => void;
  search: string;
  onSearchChange: (s: string) => void;
}

const InvoicesTab: React.FC<InvoicesTabProps> = ({ invoices, count, filter, onFilterChange, page, onPageChange, search, onSearchChange }) => {
  const pageSize = 50;
  const totalPages = Math.ceil(count / pageSize);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [searchInput, setSearchInput] = useState(search);

  const handleSearchInput = (value: string) => {
    setSearchInput(value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onSearchChange(searchInput);
  };

  const toggleDetail = async (inv: Invoice) => {
    if (expandedId === inv.id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(inv.id);
    setLoadingDetail(true);
    try {
      const resp = await fetch(`${API_BASE}/callaway/invoices/${inv.id}`);
      const data = await resp.json();
      setDetail(data);
    } catch (error) {
      console.error('Error loading invoice detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Search & Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => handleSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search invoices, customers, PO#... (Enter to search)"
            className="pl-9 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm w-72 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex items-center gap-1">
          {[
            { key: 'all', label: 'All' },
            { key: 'invoice', label: 'Invoices' },
            { key: 'titu_credit', label: 'TITU Credits' },
            { key: 'finance_charge', label: 'Finance Charges' },
            { key: 'demo_return', label: 'Demo Returns' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => onFilterChange(f.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === f.key
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="ml-auto text-sm text-gray-400">{count} records</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="w-8 px-2"></th>
                <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Type</th>
                <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Invoice #</th>
                <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Date</th>
                <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Due</th>
                <th className="text-left px-4 py-2.5 text-gray-500 font-medium">PO / Customer</th>
                <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Order #</th>
                <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Terms</th>
                <th className="text-right px-4 py-2.5 text-gray-500 font-medium">Amount</th>
                <th className="text-center px-4 py-2.5 text-gray-500 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <React.Fragment key={inv.id}>
                  <tr
                    className={`border-b border-gray-50 hover:bg-gray-50 cursor-pointer ${expandedId === inv.id ? 'bg-blue-50' : ''}`}
                    onClick={() => toggleDetail(inv)}
                  >
                    <td className="px-2 py-2.5 text-gray-400">
                      {expandedId === inv.id
                        ? <ChevronDown className="w-4 h-4" />
                        : <ChevronRight className="w-4 h-4" />
                      }
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${invoiceTypeColor[inv.invoice_type] || 'bg-gray-100 text-gray-700'}`}>
                        {invoiceTypeLabel[inv.invoice_type] || inv.invoice_type}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-700">{inv.invoice_number}</td>
                    <td className="px-4 py-2.5 text-gray-600">{fmtDate(inv.invoice_date)}</td>
                    <td className="px-4 py-2.5 text-gray-600">{fmtDate(inv.due_date)}</td>
                    <td className="px-4 py-2.5 text-gray-700">
                      {inv.customer_name || inv.po_number || '-'}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{inv.order_number || '-'}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{inv.payment_terms || '-'}</td>
                    <td className={`px-4 py-2.5 text-right font-medium ${inv.amount < 0 ? 'text-green-600' : 'text-gray-900'}`}>
                      {fmt(inv.amount)}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        inv.status === 'Open' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {inv.status}
                      </span>
                    </td>
                  </tr>
                  {expandedId === inv.id && (
                    <tr>
                      <td colSpan={10} className="bg-gray-50 px-6 py-4">
                        {loadingDetail ? (
                          <div className="flex items-center gap-2 text-gray-500 py-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading details...
                          </div>
                        ) : detail && detail.line_items.length > 0 ? (
                          <div className="space-y-3">
                            {/* Shipping info */}
                            {detail.line_items[0]?.shipping_address && (
                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3.5 h-3.5" />
                                  {detail.line_items[0].shipping_address}
                                </span>
                                {detail.line_items[0].shipping_method && (
                                  <span className="flex items-center gap-1">
                                    <Truck className="w-3.5 h-3.5" />
                                    {detail.line_items[0].shipping_method}
                                  </span>
                                )}
                              </div>
                            )}
                            {/* Line items table */}
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left py-1.5 text-gray-500 font-medium text-xs">SKU</th>
                                  <th className="text-left py-1.5 text-gray-500 font-medium text-xs">Product</th>
                                  <th className="text-center py-1.5 text-gray-500 font-medium text-xs">Qty</th>
                                  <th className="text-right py-1.5 text-gray-500 font-medium text-xs">Unit Price</th>
                                  <th className="text-right py-1.5 text-gray-500 font-medium text-xs">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detail.line_items.map((item, idx) => (
                                  <tr key={item.id || idx} className="border-b border-gray-100">
                                    <td className="py-1.5 font-mono text-xs text-gray-500">{item.sku || '-'}</td>
                                    <td className="py-1.5 text-gray-700">{item.product_name || '-'}</td>
                                    <td className="py-1.5 text-center text-gray-600">{item.quantity}</td>
                                    <td className="py-1.5 text-right text-gray-600">{fmt(item.subtotal)}</td>
                                    <td className="py-1.5 text-right font-medium text-gray-900">{fmt(item.total)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-400 py-1">No line items available for this invoice</div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <button
              onClick={() => onPageChange(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Trades Tab ─────────────────────────────────────────────────────

const TradesTab: React.FC<{ trades: TradeStatus[]; search: string; onSearchChange: (s: string) => void }> = ({ trades, search, onSearchChange }) => {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchInput, setSearchInput] = useState(search);

  const handleSearchInput = (value: string) => {
    setSearchInput(value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onSearchChange(searchInput);
  };

  const filtered = statusFilter === 'all'
    ? trades
    : trades.filter(t => statusFilter === 'pending' ? !t.credit_received : t.credit_received);

  const totalPending = trades.filter(t => !t.credit_received).reduce((s, t) => s + (t.submitted_amount || 0), 0);
  const totalReceived = trades.filter(t => t.credit_received).reduce((s, t) => s + (t.submitted_amount || 0), 0);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="text-sm text-gray-500">Total Submissions</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{trades.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="text-sm text-gray-500">Awaiting Credit</div>
          <div className="text-2xl font-bold text-orange-600 mt-1">{fmt(totalPending)}</div>
          <div className="text-xs text-gray-400 mt-0.5">{trades.filter(t => !t.credit_received).length} trade-ins</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="text-sm text-gray-500">Credits Received</div>
          <div className="text-2xl font-bold text-green-600 mt-1">{fmt(totalReceived)}</div>
          <div className="text-xs text-gray-400 mt-0.5">{trades.filter(t => t.credit_received).length} trade-ins</div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => handleSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search customer, trade #... (Enter to search)"
            className="pl-9 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex items-center gap-1">
          {[
            { key: 'all', label: 'All' },
            { key: 'pending', label: 'Awaiting Credit' },
            { key: 'received', label: 'Credit Received' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === f.key
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="ml-auto text-sm text-gray-400">{filtered.length} records</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Status</th>
                <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Customer</th>
                <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Trade #</th>
                <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Submitted</th>
                <th className="text-right px-4 py-2.5 text-gray-500 font-medium">Amount</th>
                <th className="text-center px-4 py-2.5 text-gray-500 font-medium">Credit</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      t.status === 'In Process' ? 'bg-yellow-100 text-yellow-700' :
                      t.status === 'Completed' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-700 font-medium">{t.customer_name || '-'}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{t.trade_number}</td>
                  <td className="px-4 py-2.5 text-gray-600">{fmtDate(t.date_created)}</td>
                  <td className="px-4 py-2.5 text-right text-green-600 font-medium">{fmt(t.submitted_amount)}</td>
                  <td className="px-4 py-2.5 text-center">
                    {t.credit_received ? (
                      <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                    ) : (
                      <Clock className="w-4 h-4 text-orange-400 mx-auto" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CallawayDashboard;
